package com.evoapps.evoshift.domain.model

/*
 * SH-191: SPMH, "sales per man-hour" (the owner's spreadsheet's own term, kept as the label):
 * the net sales a day (or a week, a month) brings for every hour worked. It stands next to the
 * labour % on every daily record and every summary:
 *
 *   SPMH plan   = net sales target ÷ planned hours
 *   SPMH actual = net takings ÷ hours worked (the planned hours while none are counted yet: once
 *                 the takings are in, a shift nobody clocked counts as worked as planned, SH-28)
 *   a period's SPMH = its total net sales ÷ its total hours (never an average of the days' SPMH)
 *
 * The hours are the day's priced shifts, the same hours the labour cost and the table's hours
 * columns count. The screens work it out from the record's sales and hours here (one source of
 * truth); the stored daily_records.efficiency.spmhReal is only kept for older readers.
 */
object Spmh {
    /** [netSales] ÷ [hours]; null without the sales (not entered) or without hours (nothing to divide by). */
    fun of(netSales: Double?, hours: Double): Double? {
        if (netSales == null || netSales.isNaN() || hours.isNaN() || hours <= 0.0) return null
        return netSales / hours
    }

    /** The hours a day's actual SPMH divides by: those worked, or the planned ones while none are counted. */
    fun actualHours(actualHours: Double, plannedHours: Double): Double = if (actualHours > 0.0) actualHours else plannedHours

    /** A day's planned SPMH: its net sales target ÷ its planned hours; null without a target or planned hours. */
    fun plan(target: Double, plannedHours: Double): Double? = of(target.takeIf { it > 0.0 }, plannedHours)

    /** A day's actual SPMH: its net [takings] ÷ [actualHours] (the planned ones while none); null until the takings are in. */
    fun actual(takings: Double?, actualHours: Double, plannedHours: Double): Double? =
        of(takings, actualHours(actualHours, plannedHours))

    /**
     * A period's SPMH from its days' [parts]: the sum of their sales ÷ the sum of their hours.
     * Days without sales (takings not in yet) are left out together with their hours, so the
     * hours always belong to the sales they are divided into. Null without any sales or hours.
     */
    fun total(parts: List<SpmhPart>): Double? {
        val counted = parts.filter { it.sales != null }
        if (counted.isEmpty()) return null
        return of(counted.sumOf { it.sales ?: 0.0 }, counted.sumOf { it.hours })
    }
}

/** One day's share of a period's SPMH: its net sales (null: not entered) and the hours they are divided by. */
data class SpmhPart(val sales: Double?, val hours: Double)

/** SH-191: the day's planned sales and hours, for a period's [Spmh.total]. */
val DailyRecord.spmhPlanPart: SpmhPart get() = SpmhPart(revenue.planNetto, costs.plannedHours)

/** SH-191: the day's takings and the hours worked (the planned ones while none are counted), for a period's [Spmh.total]. */
val DailyRecord.spmhActualPart: SpmhPart
    get() = SpmhPart(revenue.realNetto, Spmh.actualHours(costs.actualHours, costs.assignedPlannedHours))

/** SH-191: the days' planned SPMH: their targets ÷ their planned hours. */
fun List<DailyRecord>.spmhPlan(): Double? = Spmh.total(map { it.spmhPlanPart })

/** SH-191: the days' actual SPMH: the takings of the days with takings ÷ those days' hours. */
fun List<DailyRecord>.spmhActual(): Double? = Spmh.total(map { it.spmhActualPart })
